/* Altara COMPLAINT ASSISTANCE · slide asset (~10s)
 * Beat 1: Nomsa types the complaint.  Beat 2: NAVI probes.  Beat 3: package sealed, delivered. */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, ArrowRight, RotateCcw, Play, Send, Sparkles,
  BadgeCheck, ShieldCheck, Hash, Landmark, MessageCircle, Check,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";

const STEPS = ["What happened", "Institution", "Outcome", "Evidence"];

const COMPLAINT_TEXT =
  "The R450 000 I invested via ABC Wealth in Feb 2026 doesn't appear on my Q2 statement. I've emailed James Smith twice — no reply.";

const PROBES = [
  { q: "How much did you invest, and when did the money leave your account?",
    a: "R 450 000 · debit posted 12 Feb 2026 · reference NOMSA-ABCW-0212." },
  { q: "When did you first flag the missing balance to ABC Wealth?",
    a: "18 Aug 2026, by email to james.smith@abcwealth.co.za — no reply since." },
  { q: "What outcome would resolve this for you?",
    a: "A written confirmation my R450k is allocated + a response from ABC Wealth within 5 business days." },
];

export default function WalletAltaraComplaintDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") === "1";
  if (embed) {
    return (
      <div className="w-screen h-screen flex items-center justify-center px-8 py-6 overflow-hidden"
           style={{ background: "linear-gradient(160deg, #F5EFE6 0%, #EEF2F7 55%, #E2E8F0 100%)" }}
           data-testid="altara-complaint-embed">
        <div className="w-full max-w-5xl">
          <EmbedHeader />
          <div className="mt-4"><ComplaintStage autoplay={autoplay || true} /></div>
        </div>
        <SlideBrandMark />
      </div>
    );
  }
  return (
    <WalletShell activeTab="demos">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="altara-complaint">
        <Header />
        <DownloadStrip />
        <ComplaintStage />
        <Footnote />
      </div>
    </WalletShell>
  );
}

function Header() {
  return (
    <header>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Demo Simulation</div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>
        Months later — Nomsa raises a complaint
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-3xl">
        Six months on, R450&nbsp;000 hasn&apos;t appeared on Nomsa&apos;s statement. Altara helps her
        articulate what happened, probes to make the case complete, packages it, hash-chains
        it into her Trust Wallet, and delivers it to ABC Wealth.
      </p>
    </header>
  );
}
function EmbedHeader() {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
        Altara Consumer Intelligence · post-sale
      </div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold mt-0.5" style={{ color: NAVY }}>
        Nomsa raises a complaint
      </h1>
      <div className="text-[13px] text-slate-600 mt-1">Type · probe · package · deliver.</div>
    </div>
  );
}
function SlideBrandMark() {
  return (
    <div className="absolute bottom-3 right-5 flex items-center gap-2 text-[10px] tracking-widest uppercase"
         style={{ color: NAVY, opacity: 0.55 }}>
      <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
        <span className="font-display font-bold text-[9px]" style={{ color: GOLD }}>A</span>
      </div>
      altara.co
    </div>
  );
}
function DownloadStrip() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Slide assets</div>
        <div className="text-sm text-slate-700 mt-0.5">MP4 for modern PPT · GIF for universal embedding.</div>
      </div>
      <a href="/altara-complaint.mp4" download data-testid="dl-mp4"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white"
         style={{ background: NAVY }}>
        Download MP4 <ArrowRight className="w-3.5 h-3.5" />
      </a>
      <a href="/altara-complaint.gif" download data-testid="dl-gif"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold border"
         style={{ background: "white", borderColor: NAVY, color: NAVY }}>
        Download GIF <ArrowRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}
function Footnote() {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[12.5px] text-slate-600 leading-relaxed">
      <strong className="text-slate-800">What Altara does:</strong> articulate, organise, package and hash-chain
      the complaint. <strong className="text-slate-800">What Altara does not:</strong> investigate, adjudicate
      or manage the case. The institution — and, if unresolved, the Ombud — own the outcome.
    </div>
  );
}

/* ─── the main stage ─── */
function ComplaintStage({ autoplay = false }) {
  const [phase, setPhase] = useState("idle");   // idle | typing | probing | packaging | sent
  const [typed, setTyped] = useState("");
  const [probeIdx, setProbeIdx] = useState(-1); // -1..2
  const [pkgStep, setPkgStep] = useState(0);    // 0 hidden · 1 preview · 2 sending · 3 sent
  const timers = useRef([]);
  const fired = useRef(false);
  const t = (ms, fn) => timers.current.push(setTimeout(fn, ms));
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const play = () => {
    if (phase !== "idle" && phase !== "sent") return;
    clear();
    setTyped(""); setProbeIdx(-1); setPkgStep(0); setPhase("typing");
    // Beat 1 · typing (0 → 2.6s)
    const chars = COMPLAINT_TEXT.length;
    const perChar = 2400 / chars;
    for (let i = 1; i <= chars; i++) t(i * perChar, () => setTyped(COMPLAINT_TEXT.slice(0, i)));
    // Beat 2 · NAVI probes (2.8 → 6.4s)
    t(2800, () => { setPhase("probing"); setProbeIdx(0); });
    t(4000, () => setProbeIdx(1));
    t(5200, () => setProbeIdx(2));
    // Beat 3 · packaging (6.6 → 9.6s)
    t(6600, () => { setPhase("packaging"); setPkgStep(1); });
    t(8100, () => setPkgStep(2));       // sending…
    t(9200, () => { setPkgStep(3); setPhase("sent"); });
  };
  const reset = () => { clear(); setPhase("idle"); setTyped(""); setProbeIdx(-1); setPkgStep(0); };

  useEffect(() => {
    if (!autoplay || fired.current) return;
    fired.current = true;
    setTimeout(play, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);
  useEffect(() => () => clear(), []);

  const activeStep = phase === "typing" ? 0 : phase === "probing" ? 1 : phase === "packaging" || phase === "sent" ? 3 : 0;

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden relative"
         data-testid="complaint-stage">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ background: `${GOLD}22` }}>
          <FileText className="w-5 h-5" style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Consumer assistance</div>
          <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>Complaint assistance</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] tracking-widest uppercase text-slate-400">Filed</div>
          <div className="text-[11px] font-medium text-slate-700">24 August 2026</div>
        </div>
      </div>
      {/* Stepper */}
      <div className="px-6 pt-4">
        <Stepper active={activeStep} allDone={phase === "sent"} />
      </div>
      {/* Body */}
      <div className="px-6 pb-6 pt-5">
        <AnimatePresence mode="wait">
          {(phase === "idle" || phase === "typing") && (
            <motion.div key="typing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}>
              <TypingPanel typed={typed} typing={phase === "typing"} />
            </motion.div>
          )}
          {phase === "probing" && (
            <motion.div key="probing" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.35 }}>
              <ProbingPanel probeIdx={probeIdx} />
            </motion.div>
          )}
          {(phase === "packaging" || phase === "sent") && (
            <motion.div key="pkg" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}>
              <PackagePanel pkgStep={pkgStep} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Controls */}
      <div className="px-6 pb-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <div className="text-[11px] text-slate-500">
          <strong>What Altara does:</strong> articulate · organise · package. Institution owns the outcome.
        </div>
        <div className="flex items-center gap-2">
          {phase !== "idle" && (
            <button onClick={reset} data-testid="reset-complaint"
                    className="w-8 h-8 rounded-full flex items-center justify-center border"
                    style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={play} disabled={phase === "typing" || phase === "probing" || phase === "packaging"}
                  data-testid="play-complaint"
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                  style={{ background: NAVY }}>
            <Play className="w-3.5 h-3.5" /> {phase === "idle" ? "Play" : phase === "sent" ? "Replay" : "Playing…"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Stepper ─── */
function Stepper({ active, allDone }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {STEPS.map((label, i) => {
        const done = allDone || i < active;
        const isActive = !allDone && i === active;
        return (
          <div key={label} className="flex items-center gap-2">
            <motion.div layout
              animate={{
                background: done ? "#059669" : isActive ? NAVY : "white",
                color: done || isActive ? "white" : "#94a3b8",
                borderColor: done ? "#059669" : isActive ? NAVY : "#E2E8F0",
              }}
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border">
              {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </motion.div>
            <div className="text-[11px] font-semibold" style={{ color: done ? "#065F46" : isActive ? NAVY : "#94a3b8" }}>
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Beat 1 · typing ─── */
function TypingPanel({ typed, typing }) {
  return (
    <div>
      <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>Step 1 · What happened?</div>
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold mt-3" style={{ color: GOLD }}>
        Describe what happened — in your own words
      </div>
      <div className="mt-2 rounded-lg border p-3 min-h-[100px] text-[13.5px] leading-relaxed"
           style={{ borderColor: typing ? GOLD : "#E2E8F0", background: "#FDFCF9", color: NAVY,
                    boxShadow: typing ? `0 0 0 3px ${GOLD}33` : "none" }}
           data-testid="complaint-textarea">
        {typed}
        {typing && <span className="inline-block w-[2px] h-[1em] align-text-bottom ml-0.5"
                         style={{ background: NAVY, animation: "cbl 0.8s steps(2) infinite" }} />}
        {!typed && !typing && <span className="text-slate-400">Give us the story — dates, amounts, what was said…</span>}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: GOLD }}>When did this happen?</div>
          <div className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px]" style={{ color: NAVY, background: "#FDFCF9" }}>
            {typed.length > 60 ? "24 August 2026" : <span className="text-slate-400">e.g. 2 May 2026, last Tuesday</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: GOLD }}>Where did it happen?</div>
          <div className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px]" style={{ color: NAVY, background: "#FDFCF9" }}>
            {typed.length > 60 ? "Email" : "—"}
          </div>
        </div>
      </div>
      <style>{`@keyframes cbl { 0%,50% {opacity:1} 51%,100% {opacity:0} }`}</style>
    </div>
  );
}

/* ─── Beat 2 · NAVI probing ─── */
function ProbingPanel({ probeIdx }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: NAVY }}>
          <Sparkles className="w-4 h-4" style={{ color: GOLD }} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: GOLD }}>
            NAVI · strengthening your case
          </div>
          <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>
            Three quick clarifiers before we package this.
          </div>
        </div>
      </div>
      <div className="space-y-2.5">
        {PROBES.map((p, i) => (
          <AnimatePresence key={i}>
            {i <= probeIdx && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          transition={{ duration: 0.35 }}
                          className="rounded-xl border p-3 space-y-2"
                          style={{ borderColor: "#E2E8F0", background: "#FAFCFF" }}
                          data-testid={`probe-${i}`}>
                <div className="flex items-start gap-2 text-[12.5px]">
                  <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: NAVY, color: "white" }}>NAVI</span>
                  <span style={{ color: NAVY, fontWeight: 600 }}>{p.q}</span>
                </div>
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.3 }}
                            className="flex items-start gap-2 text-[12.5px] pl-1">
                  <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: GOLD, color: NAVY }}>NOMSA</span>
                  <span className="text-slate-700">{p.a}</span>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        ))}
      </div>
    </div>
  );
}

/* ─── Beat 3 · Package ─── */
function PackagePanel({ pkgStep }) {
  return (
    <div>
      {pkgStep < 3 && (
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}22` }}>
            <Sparkles className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: GOLD }}>Altara · packaging</div>
            <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>
              {pkgStep === 2 ? "Delivering to ABC Wealth…" : "Sealing your complaint bundle."}
            </div>
          </div>
        </div>
      )}
      {pkgStep === 3 && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border p-3 mb-3 flex items-center gap-3"
                    style={{ background: "#ECFDF5", borderColor: "#6EE7B7" }}
                    data-testid="delivered-banner">
          <BadgeCheck className="w-5 h-5" style={{ color: "#059669" }} />
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "#065F46" }}>
              Delivered · Ombud-ready copy filed
            </div>
            <div className="text-[12.5px] font-semibold" style={{ color: NAVY }}>
              Bundle ALT-COMP-2026-08-04A29 sent to ABC Wealth. Filed in Nomsa&apos;s Trust Wallet.
            </div>
          </div>
        </motion.div>
      )}
      <div className="rounded-xl border border-slate-200 p-4"
           style={{ background: "linear-gradient(180deg, #FFFDF6 0%, white 100%)" }}
           data-testid="package-preview">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: GOLD }}>Complaint bundle</div>
          <div className="text-[10px] font-mono" style={{ color: NAVY }}>ALT-COMP-2026-08-04A29</div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Cell label="Institution" value="ABC Wealth · FSP #48291" Icon={Landmark} />
          <Cell label="Adviser named" value="James Smith · Rep #A-2214-JS" Icon={ShieldCheck} />
          <Cell label="What happened" value="R450k invested Feb 2026 not on Q2 statement · 2 unanswered emails" Icon={FileText} />
          <Cell label="Outcome sought" value="Written confirmation of allocation + reply within 5 days" Icon={MessageCircle} />
          <Cell label="Evidence attached" value="Trust Certificate ATC-2026-09-A7C4D9F1" Icon={Hash} />
          <Cell label="Delivery" value={pkgStep >= 2 ? "Email · api.abcwealth.co.za/complaints" : "queued"} Icon={Send} />
        </div>
        {pkgStep === 2 && (
          <div className="mt-3 rounded-lg p-2 flex items-center gap-2 text-[11.5px]"
               style={{ background: `${GOLD}18`, color: NAVY }}>
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: GOLD }} />
            Encrypting bundle · appending to Nomsa&apos;s Trust Wallet hash chain…
          </div>
        )}
      </div>
    </div>
  );
}
function Cell({ label, value, Icon }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white px-2.5 py-2">
      <div className="text-[9px] tracking-widest uppercase text-slate-400 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-[11.5px] font-medium mt-0.5 leading-snug" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}
